export const leverageMargin = {
    id: 'leverage-margin',
    title: 'Leverage and margin',
    summary: 'How leverage sizes a trade, how much money it sets aside, and what happens when an account runs low.',
    level: 'beginner',
    art: 'gold',
    image: { src: '/academy/leverage-margin.webp', alt: 'A gold coin above gold bars and a stack of gold coins' },
    screen: { to: '/trade', label: 'Trade' },
    lessons: [
        {
            id: 'what-leverage-does',
            title: 'What leverage does',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Leverage lets you open a trade that is bigger than the money you put up for it. At **1:10**, every $1 you set aside controls $10 of the market.' },
                { type: 'p', text: 'Leverage does not change how much a price move is worth. A trade worth $5,000 gains or loses $50 on a 1% move whether you opened it at 1:2 or 1:50. What leverage changes is how much of your money is tied up, and so how big a trade your balance can carry.' },
                { type: 'example', title: 'Same trade, different leverage', text: 'You buy $5,000 of gold. At 1:5 the trade sets aside $1,000; at 1:50 it sets aside $100. A 2% fall in gold costs you $100 in both cases. At 1:50 that is your whole margin for this trade.' },
                { type: 'p', text: 'Each instrument has a minimum, default and maximum leverage. The order panel opens at the default; you can choose anything between the minimum and the maximum. Changing leverage applies to new orders only: a trade keeps the leverage it was opened with.' },
                { type: 'warn', text: 'High leverage makes it easy to open trades that are large compared with your balance. Losses grow with the size of the trade, not with the margin you set aside.' },
            ],
            quiz: [
                {
                    q: 'You open a $2,000 trade. The price moves 1% against you. How much do you lose at 1:20 leverage?',
                    options: ['$1', '$20', '$400'],
                    answer: 1,
                    why: '1% of the $2,000 trade is $20. Leverage changes the margin, not the value of a move.',
                },
                {
                    q: 'You change leverage in the order panel. What happens to trades you already have open?',
                    options: ['They switch to the new leverage', 'They keep the leverage they were opened with', 'They are closed'],
                    answer: 1,
                    why: 'Leverage is fixed when a trade opens. A new setting applies to new orders only.',
                },
            ],
            tryIt: { to: '/trade/BTC', label: 'See leverage in the order panel' },
            terms: ['leverage', 'notional'],
        },
        {
            id: 'margin-used',
            title: 'How much margin a trade uses',
            minutes: 4,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Margin is the money set aside to keep a trade open. It is the trade size divided by your leverage.' },
                { type: 'example', title: 'Margin = trade size ÷ leverage', text: 'A $5,000 trade at 1:10 uses $500 of margin. The same trade at 1:25 uses $200.' },
                { type: 'calc', calc: 'margin' },
                { type: 'p', text: 'A fee is also charged when the trade opens and again when it closes. The order panel shows the margin and the fee before you place the order, so you always see the full amount the trade needs.' },
                { type: 'p', text: 'Margin is not a cost. When you close the trade the margin is released, and only your profit or loss, the fees and any swap change your balance.' },
                { type: 'tip', text: 'Hover the “?” next to Margin in the order panel to see the numbers for the trade you are about to place.' },
            ],
            quiz: [
                {
                    q: 'A $2,000 trade at 1:20 uses how much margin?',
                    options: ['$40', '$100', '$2,000'],
                    answer: 1,
                    why: '$2,000 ÷ 20 = $100.',
                },
                {
                    q: 'What happens to the margin when you close a trade?',
                    options: ['It is kept as a fee', 'It is released back to your available money', 'It moves to your demo account'],
                    answer: 1,
                    why: 'Margin is only set aside while the trade is open. Closing releases it.',
                },
            ],
            tryIt: { to: '/trade/BTC', label: 'Check the margin on a BTC order' },
            terms: ['margin', 'fee'],
        },
        {
            id: 'cross-margin',
            title: 'Cross margin: one balance backs every trade',
            minutes: 5,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Margin on {brand} is **cross**: your account’s whole equity backs every open trade. There is no separate pot of money per trade.' },
                { type: 'list', items: ['**Equity** = balance + credit + the profit or loss of your open trades (swap included).', '**Margin in use** = the margin of all your open trades added together.', '**Free margin** = what is left for new trades and withdrawals.'] },
                { type: 'p', text: 'Because the balance is shared, a losing trade reduces the free margin available to every other trade, and a winning trade adds to it.' },
                { type: 'example', title: 'Two trades, one balance', text: 'Your balance is $2,000. You have a BTC trade using $300 of margin and a gold trade using $200. Gold is down $150 and BTC is up $50. Equity is $1,900, margin in use is $500, and free margin is $1,400.' },
                { type: 'warn', text: 'A loss on one trade is not limited to that trade’s margin. If it keeps growing, it uses up equity that backs your other trades too.' },
            ],
            quiz: [
                {
                    q: 'Balance $1,000, one trade using $200 margin, floating loss $100. What is your equity?',
                    options: ['$1,000', '$900', '$700'],
                    answer: 1,
                    why: 'Equity = balance + floating P&L = $1,000 − $100 = $900.',
                },
                {
                    q: 'With cross margin, a big loss on one trade…',
                    options: ['only affects that trade', 'reduces the free margin for all your trades', 'is paid by the platform'],
                    answer: 1,
                    why: 'Your whole equity backs every trade, so any loss reduces the free margin for all of them.',
                },
            ],
            tryIt: { to: '/wallet', label: 'See your equity and free margin' },
            terms: ['equity', 'free-margin', 'cross-margin'],
        },
        {
            id: 'margin-level',
            title: 'Margin level and alerts',
            minutes: 5,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'Your **margin level** compares equity with the margin in use: margin level = equity ÷ margin in use. At 100% your equity exactly equals the margin your trades need.' },
                { type: 'calc', calc: 'marginLevel' },
                { type: 'p', text: 'We warn you before it gets serious. You get a **warning** when the margin level falls to {warning} and a **critical** alert at {critical}. Each alert is sent once and can fire again after the level has recovered.' },
                { type: 'list', items: ['Close a losing trade to release its margin.', 'Close one of several trades instead of all of them: each order is its own position.', 'Deposit more money to raise equity.'] },
                { type: 'tip', text: 'Watch the margin level on the Portfolio page. It moves with every price change while trades are open.' },
            ],
            quiz: [
                {
                    q: 'Equity $600, margin in use $1,000. What is the margin level?',
                    options: ['60%', '166%', '40%'],
                    answer: 0,
                    why: '$600 ÷ $1,000 = 60%.',
                },
                {
                    q: 'Which of these raises your margin level?',
                    options: ['Opening another trade', 'Depositing money', 'Raising leverage on open trades'],
                    answer: 1,
                    why: 'A deposit raises equity. A new trade adds margin in use, which lowers the level, and open trades keep their leverage.',
                },
            ],
            tryIt: { to: '/portfolio', label: 'Find your margin level' },
            terms: ['margin-level'],
        },
        {
            id: 'stop-out',
            title: 'Stop-out and negative-balance protection',
            minutes: 5,
            level: 'beginner',
            blocks: [
                { type: 'p', text: 'If the margin level falls to **{stopOut}**, the account is stopped out: the platform starts closing trades for you to protect what is left.' },
                { type: 'steps', items: ['The trade with the largest loss is closed first.', 'The margin level is checked again on the next price.', 'If it is still at or below {stopOut}, the next trade with the largest loss is closed, until the level recovers.'] },
                { type: 'example', title: 'When a stop-out starts', text: 'Margin in use is $1,000, so the stop-out begins when equity falls to $300 ({stopOut} of $1,000).' },
                { type: 'p', text: 'Prices can move fast, so a trade may close at a worse price than the stop-out level. If your balance goes below zero once no trades are open, we write the negative balance off. You can never owe us more than you deposited.' },
                { type: 'warn', text: 'A stop-out closes trades at a loss. Use stop losses and keep your margin level well above the alerts so you decide when to close, not the platform.' },
            ],
            quiz: [
                {
                    q: 'During a stop-out, which trade is closed first?',
                    options: ['The newest one', 'The one with the largest loss', 'All trades at once'],
                    answer: 1,
                    why: 'The trade with the largest loss is closed first, one at a time, until the margin level recovers.',
                },
                {
                    q: 'Your balance is below zero after a fast market and you have no open trades. What happens?',
                    options: ['You must pay it back', 'We write it off', 'It is taken from your next deposit'],
                    answer: 1,
                    why: 'Negative-balance protection: once no trades are open, a negative balance is written off.',
                },
            ],
            tryIt: { to: '/portfolio', label: 'Review your open trades' },
            terms: ['stop-out', 'negative-balance-protection'],
        },
    ],
};
export const leverageMarginTerms = [
    { id: 'leverage', term: 'Leverage', text: 'How many times bigger a trade is than the margin set aside for it. At 1:10, $100 of margin opens a $1,000 trade.', lesson: 'what-leverage-does' },
    { id: 'notional', term: 'Notional (trade size)', text: 'The full value of a trade in USDT: quantity × price. Profit, loss and fees are worked out from it.', lesson: 'what-leverage-does' },
    { id: 'margin', term: 'Margin', text: 'Money set aside to keep a trade open: trade size ÷ leverage. It is released when the trade closes.', lesson: 'margin-used' },
    { id: 'equity', term: 'Equity', text: 'Balance + credit + the profit or loss of your open trades, swap included. What your account is worth right now.', lesson: 'cross-margin' },
    { id: 'free-margin', term: 'Free margin', text: 'Equity minus the margin in use and minus money held for pending orders. What you can use for new trades or withdraw.', lesson: 'cross-margin' },
    { id: 'cross-margin', term: 'Cross margin', text: 'Your whole equity backs every open trade, so a loss on one trade reduces the free margin for all of them.', lesson: 'cross-margin' },
    { id: 'margin-level', term: 'Margin level', text: 'Equity ÷ margin in use, as a percentage. Alerts are sent at {warning} and {critical}; the stop-out starts at {stopOut}.', lesson: 'margin-level' },
    { id: 'stop-out', term: 'Stop-out', text: 'When the margin level reaches {stopOut}, the trade with the largest loss is closed first, then the next, until the level recovers.', lesson: 'stop-out' },
    { id: 'negative-balance-protection', term: 'Negative-balance protection', text: 'If your balance is below zero once no trades are open, we write it off. You never owe more than you deposited.', lesson: 'stop-out' },
];
//# sourceMappingURL=leverage-margin.js.map